/**
 * Sursă unică pentru structura Hub 3D — trebuie să rămână aliniată cu sidebar-ul din templates/index.html.
 */

import type { UserRole } from './types';

export type NavMinRole = UserRole;

export type HubNavLeaf = {
    kind: 'section';
    section: string;
    label: string;
    icon: string;
    subtitle?: string;
    minRole: NavMinRole;
};

export type HubNavModal = {
    kind: 'modal';
    modal: 'users';
    label: string;
    icon: string;
    subtitle?: string;
    minRole: NavMinRole;
};

export type HubNavLink = {
    kind: 'link';
    href: string;          // relativ la rădăcina aplicației (prefixul se adaugă la click)
    label: string;
    icon: string;
    subtitle?: string;
    minRole: NavMinRole;
};

export type HubNavGroup = {
    kind: 'group';
    id: string;
    label: string;
    icon: string;
    subtitle?: string;
    minRole: NavMinRole;
    children: HubNavNode[];
};

export type HubNavNode = HubNavLeaf | HubNavModal | HubNavLink | HubNavGroup;

const ROLE_LEVEL: Record<NavMinRole, number> = { operator: 1, engineer: 2, master: 3 };

/** Arbore complet (înainte de filtrare pe rol). */
export const HUB_NAV_TREE: HubNavNode[] = [
    {
        kind: 'group',
        id: 'op',
        label: 'Acces rapid',
        icon: '📋',
        subtitle: 'Operator',
        minRole: 'operator',
        children: [
            { kind: 'section', section: 'dashboard', label: 'Dashboard Producție', icon: '📋', subtitle: 'Linii & stare', minRole: 'operator' },
            { kind: 'section', section: 'mytasks', label: 'Lucrările mele', icon: '🔧', subtitle: 'Task-uri zilnice', minRole: 'operator' },
            { kind: 'section', section: 'journal', label: 'Intervenții urgente', icon: '🚨', subtitle: 'Urgențe', minRole: 'operator' },
            { kind: 'section', section: 'parts', label: 'Piese & identificare', icon: '🔩', subtitle: 'Catalog', minRole: 'operator' },
            { kind: 'section', section: 'parts-order', label: 'Piese de comandat', icon: '🛒', subtitle: 'Aprovizionare', minRole: 'operator' },
            { kind: 'section', section: 'alarm_history', label: 'Istoric alarme', icon: '🔔', subtitle: 'Evenimente', minRole: 'operator' },
            { kind: 'section', section: 'chat', label: 'Chat AI', icon: '🤖', subtitle: 'Asistent fabrică', minRole: 'operator' },
            { kind: 'section', section: 'manuals', label: 'Manuale', icon: '📖', subtitle: 'Documentație mașini', minRole: 'operator' },
            { kind: 'section', section: 'ghid', label: 'Ghid de folosire', icon: '❓', subtitle: 'Cum folosești aplicația', minRole: 'operator' },
        ],
    },
    {
        kind: 'group',
        id: 'eng',
        label: 'Inginer',
        icon: '⚙️',
        subtitle: 'Planificare & diag.',
        minRole: 'engineer',
        children: [
            { kind: 'section', section: 'schedule-intervention', label: 'Programează intervenție', icon: '📌', minRole: 'engineer' },
            { kind: 'section', section: 'calendar', label: 'Calendar mentenanță', icon: '📅', minRole: 'engineer' },
            { kind: 'section', section: 'procedures', label: 'Fișe de lucru', icon: '📋', minRole: 'engineer' },
            { kind: 'section', section: 'validation', label: 'De validat', icon: '✅', minRole: 'engineer' },
            { kind: 'section', section: 'confirmari', label: 'De confirmat', icon: '☑️', minRole: 'engineer' },
            { kind: 'section', section: 'diagnostic', label: 'Diagnostic & erori', icon: '🔍', minRole: 'engineer' },
            { kind: 'section', section: 'ar', label: 'AR vizualizare', icon: '📷', minRole: 'operator' },
            { kind: 'section', section: 'reports', label: 'Rapoarte', icon: '📑', minRole: 'engineer' },
            { kind: 'section', section: 'statistics', label: 'Statistici', icon: '📊', minRole: 'engineer' },
            { kind: 'link', href: '/live/monitor', label: 'Transmisii live', icon: '🔴', subtitle: 'POV mecanici', minRole: 'engineer' },
        ],
    },
    {
        kind: 'group',
        id: 'adm',
        label: 'Administrare',
        icon: '👑',
        subtitle: 'Master',
        minRole: 'master',
        children: [
            { kind: 'section', section: 'overview', label: 'Overview fabrică', icon: '🏭', subtitle: 'Config & hală', minRole: 'master' },
            { kind: 'modal', modal: 'users', label: 'Utilizatori', icon: '👥', subtitle: 'Gestionare conturi', minRole: 'master' },
            { kind: 'section', section: 'golden-set', label: 'Evaluare AI', icon: '🧪', subtitle: 'Golden Set', minRole: 'master' },
            { kind: 'section', section: 'sistem-sanatate', label: 'Sănătate sistem', icon: '🩺', subtitle: 'Costuri & incidente', minRole: 'master' },
        ],
    },
];

function userMeetsMin(role: string | undefined, min: NavMinRole): boolean {
    const u = ROLE_LEVEL[(role as NavMinRole) || 'operator'] || 1;
    return u >= ROLE_LEVEL[min];
}

function filterChildren(nodes: HubNavNode[], userRole: string | undefined): HubNavNode[] {
    const out: HubNavNode[] = [];
    for (const n of nodes) {
        if (n.kind === 'group') {
            if (!userMeetsMin(userRole, n.minRole)) continue;
            const ch = filterChildren(n.children, userRole);
            if (ch.length === 0) continue;
            out.push({ ...n, children: ch });
            continue;
        }
        if (!userMeetsMin(userRole, n.minRole)) continue;
        out.push(n);
    }
    return out;
}

/** Arbore Hub filtrat după rolul utilizatorului curent. */
export function getHubNavTreeForRole(userRole: string | undefined): HubNavNode[] {
    return filterChildren(HUB_NAV_TREE, userRole);
}
